import React from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import useStore from 'Hooks/useStore';
import { appConfigStore } from 'Models/AppConfigStore';
import {
	isTripClosedByBookingType,
	isTripCompletelyClosedFromBookings,
} from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { store } from 'Models/Store';
import { LegOptionContext } from 'Modules/MultiStopWizard/Components/Leg/Contexts/LegOptionContext';
import { isWalkOnNotOffered, walkOnNotOfferedMessage } from 'Modules/MultiStopWizard/Hooks/walkOnNotOffered';
import LegOptionCapacities from 'Modules/MultiStopWizard/Components/Leg/LegOptionCapacities';
import StopSeriesVisualiser from 'Modules/MultiStopWizard/Components/Leg/StopSeriesVisualiser';
import LegOptionWaitlist from 'Modules/MultiStopWizard/Components/Leg/LegOptionWaitlist';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { formatStandardTimeParts } from 'Util/_HumanWritten/TimeUtils';
import { stringNotEmpty } from 'Util/TypeGuards';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import Skeleton from 'Views/Components/_HumanWritten/Skeleton/Skeleton';
import { hasDeparted, isSoldOutButBookable, soldOutMargin } from './tripOptionState';
import TripOptionNotes from './TripOptionNotes';
import TripOptionStatusInfo from './TripOptionStatusInfo';

/**
 * The pieces every variant of the redesigned trip card is built from.
 *
 * <p>Four states share them — the row in the list, the sold-out row, the chosen summary and the
 * phone's stacked card — and each of those draws the same facts at a different size. Keeping the
 * pieces here is what stops the phone's price and the desktop's price from drifting into two
 * different ideas of what a price is.</p>
 */

/** The voyage number, where the site runs them and this reader is allowed to see them. */
export function TripOptionVoyage() {
	const context = React.useContext(LegOptionContext);
	const name = context?.trip?.name;

	if (
		!stringNotEmpty(name) ||
		!appConfigStore.showVoyageNumberInput ||
		!(appConfigStore.voyageNumberShowToCustomers || store.isStaff)
	) {
		return null;
	}

	return <span className="trip-option__voyage">{name.toUpperCase()}</span>;
}

interface TimesProps {
	/** `lg` is the chosen card's 26px pair; `md` the list row's 20px; `sm` the phone row's 17px. */
	size: 'sm' | 'md' | 'lg';
}

/**
 * Whether each time says which end of the journey it is.
 *
 * <p>Trialled rather than settled: the pair reads as a journey without them — left is when it
 * leaves, right is when it lands, the way every timetable is written — and two more lines of small
 * caps is two more things on a row that is mostly small caps already. <b>Set this to `false` to
 * take them back out; nothing else needs changing.</b></p>
 */
const SHOW_TIME_LABELS = true;

/** A time, with AM / PM set small and grey so a 12-hour time is barely wider than a 24-hour one. */
function TimeFigure({ date }: { date?: Date | string }) {
	const { time, period } = formatStandardTimeParts(date);
	return (
		<span className="trip-option__time-figure">
			{time}
			{period && <span className="trip-option__time-period">{period}</span>}
		</span>
	);
}

/** Departure and arrival, with the run between them. */
export function TripOptionTimes({ size }: TimesProps) {
	const context = React.useContext(LegOptionContext);

	if (!context) {
		return null;
	}

	const { departureTime, arrivalTime, numberOfStopsBetween } = context;

	return (
		<div
			className={classNames(`trip-option__times trip-option__times--${size}`, {
				'trip-option__times--labelled': SHOW_TIME_LABELS,
			})}
		>
			<span className="trip-option__time">
				{SHOW_TIME_LABELS && (
					<em className="trip-option__time-label">
						<FormattedMessage id="trip-option__depart__label" defaultMessage="Depart" />
					</em>
				)}
				<TimeFigure date={departureTime} />
			</span>
			{/*
				The same run the old card drew, kept rather than redrawn as the design's plain dotted
				line: it is the one thing on the row that says a sailing calls somewhere on the way,
				and a route with four stops reads as four stops rather than as a decoration.
			*/}
			<StopSeriesVisualiser
				numberOfStops={numberOfStopsBetween}
				variant="contrast"
				hideStopCountLabel
				useMaterialIcons
			/>
			<span className="trip-option__time">
				{SHOW_TIME_LABELS && (
					<em className="trip-option__time-label">
						<FormattedMessage id="trip-option__arrive__label" defaultMessage="Arrive" />
					</em>
				)}
				<TimeFigure date={arrivalTime} />
			</span>
		</div>
	);
}

/** `✓ Your departing trip` — the chosen card's eyebrow. */
export function TripOptionChosenLabel() {
	const context = React.useContext(LegOptionContext);

	if (!context) {
		return null;
	}

	return (
		<span className="trip-option__chosen-label">
			<span className="trip-option__chosen-tick" aria-hidden="true">
				<Icon name="check" useMaterialIcons />
			</span>
			{context.legType === 'departure' ? (
				<FormattedMessage
					id="trip-option__your-departing-trip__label"
					defaultMessage="Your departing trip"
				/>
			) : (
				<FormattedMessage
					id="trip-option__your-returning-trip__label"
					defaultMessage="Your returning trip"
				/>
			)}
		</span>
	);
}

/**
 * `Thu 17 Sep · 1 stop` — the date under the chosen card's times.
 *
 * <p>On the list the day is the rail's job and every row below it shares one. On the chosen card
 * the rail is gone, so the card has to carry its own date — it is the thing a booker comes back to
 * the step to check, and the one they screenshot.</p>
 */
export function TripOptionWhen() {
	const context = React.useContext(LegOptionContext);

	if (!context) {
		return null;
	}

	const { departureTime, numberOfStopsBetween } = context;
	const stops = numberOfStopsBetween ?? 0;

	return (
		<span className="trip-option__when">
			{moment(departureTime).format('ddd D MMM')}
			{stops > 0 && (
				<>
					{' · '}
					<FormattedMessage
						id="trip-option__stops__label"
						defaultMessage="{count, plural, one {# stop} other {# stops}}"
						values={{ count: stops }}
					/>
				</>
			)}
		</span>
	);
}

/**
 * What is left on the sailing — or why it cannot be taken.
 *
 * <p>One or the other, never both. The error is the answer to "can I have this sailing", and a row
 * of capacity chips beside it competes with that answer rather than adding to it. The waitlist
 * button is the way out of the error, so it sits with it.</p>
 */
interface SpaceProps {
	/**
	 * Something to sit on the `Space left` line, at the right of it.
	 *
	 * <p>The phone's price, or on a sold-out sailing the way onto its waitlist. On a desktop the
	 * price is a column of its own beside this one, which at 375px leaves the chips about 190px —
	 * and a client's capacity names run to `Electric Vehicle`, so nothing pairs and every chip takes
	 * a line. Passed in here instead, the chips get the width of the card and wrap two and three to
	 * a row as the design draws them.</p>
	 */
	aside?: React.ReactNode;
	/**
	 * A mark at the head of the line, before the label.
	 *
	 * <p>The phone's availability dot, which stands in for the rail a 375px card has no room for.
	 * It goes <b>in the line</b> rather than beside the block, so flexbox centres it on the text it
	 * marks. As a sibling of the whole column it was centred against the column's height instead,
	 * which differs with every state — eleven pixels out on a sold-out sailing, whose button made
	 * that column twice as tall as the words beside the dot.</p>
	 */
	lead?: React.ReactNode;
	/**
	 * Whether the waitlist button sits on this line.
	 *
	 * <p>Off where the caller has put it in `aside` instead, so a sold-out sailing does not offer
	 * the same button twice.</p>
	 */
	showWaitlist?: boolean;
	/** Fold the chips after this many behind `+N more` — see `collapseAfter` on `LegOptionCapacities`. */
	collapseChipsAfter?: number;
}

export function TripOptionSpace({ aside, lead, showWaitlist = true, collapseChipsAfter }: SpaceProps) {
	const context = React.useContext(LegOptionContext);
	const { isStaff } = useStore();

	if (!context) {
		return null;
	}

	const { errorMessage, trip, wizardData } = context;


	return (
		<div className="trip-option__space">
			<div className="trip-option__space-head">
				<span className="trip-option__space-lead">
					{lead}
					{stringNotEmpty(errorMessage) ? (
						<span className="trip-option__status">
							{errorMessage}
							{/*
								Explains whichever status is showing — see `TripOptionStatusInfo`.
								Staff are given none of these messages, so it has nothing to add
								for them.
							*/}
							{!isStaff && <TripOptionStatusInfo />}
						</span>
					) : (
						<span className="trip-option__space-label">
							<FormattedMessage id="trip-cards__spaces-left__label" defaultMessage="Space left" />
						</span>
					)}
				</span>
				{showWaitlist && <LegOptionWaitlist />}
			</div>

			{/*
				A sibling of the head, not a child of it.

				Inside the head it set that line's height, so on a phone — where the head carries the
				price and the notes pill — the chips began below a block three lines tall and sat
				marooned under `Space left`. As a sibling the phone can lay the two out as columns,
				with the chips directly beneath the label they belong to.
			*/}
			{aside}

			{!stringNotEmpty(errorMessage) && (
				<div className="trip-option__chips">
					<LegOptionCapacities collapseAfter={collapseChipsAfter} />
				</div>
			)}
		</div>
	);
}

/**
 * The right-hand column: what it costs, and anything else about the sailing itself.
 *
 * <p>The notes pill used to sit among the capacity chips, which put a fact about the sailing in a
 * row of facts about its space — and on a busy row it was the sixth chip, read last if at all.
 * Here it has the width of the price column to itself and sits where the eye already finishes.</p>
 */
export function TripOptionAside({ children }: { children?: React.ReactNode }) {
	return (
		<div className="trip-option__aside">
			{children}
			<TripOptionNotes />
		</div>
	);
}

interface PriceProps {
	/** `lg` is the chosen card's 24px figure; `md` the list row's 20px. */
	size: 'sm' | 'md' | 'lg';
}

/** `From` over the cheapest this sailing can be had for, or the change it makes to an edited bill. */
export function TripOptionPrice({ size }: PriceProps) {
	const context = React.useContext(LegOptionContext);

	if (!context) {
		return null;
	}

	const { isLoading, price, wizardData, hidePrice } = context;

	if (hidePrice) {
		return null;
	}

	if (isLoading || price === undefined) {
		return (
			<div className={classNames('trip-option__price', `trip-option__price--${size}`)}>
				<Skeleton width="5rem" height="2rem" />
			</div>
		);
	}

	const isChange = wizardData.wizardMode === 'ALTERATION';

	return (
		<div className={classNames('trip-option__price', `trip-option__price--${size}`)}>
			{/*
				No "From" over a difference. On an edit the figure is what this sailing adds to or
				takes off a bill that is already paid, and the word would read it back as a fare.
			*/}
			{!isChange && (
				<span className="trip-option__price-label">
					<FormattedMessage id="trip-option__from-price__label" defaultMessage="From" />
				</span>
			)}
			<span className="trip-option__price-figure">{formatPriceDisplay(price, isChange)}</span>
		</div>
	);
}

/** The note's classes: block under the times in the list, inline on the date line once chosen. */
function noteClass(inline: boolean, over = false): string {
	return classNames('trip-option__sold-out', {
		'trip-option__sold-out--inline': inline,
		'trip-option__sold-out--over': over,
	});
}

/**
 * What this site calls a booking type.
 *
 * <p>The same keys the booking-type tabs are labelled from, so the word in this note is the word on
 * the button the reader pressed to get here — including where a client has renamed it.</p>
 */
function bookingTypeName(tab: string, intl: IntlShape): string {
	if (tab === 'vehicle') {
		return intl.formatMessage({ id: 'vehicle__name__label', defaultMessage: 'Vehicle' });
	}
	if (tab === 'freight') {
		return intl.formatMessage({ id: 'freight__name__label', defaultMessage: 'Freight' });
	}
	return intl.formatMessage({ id: 'passenger__name__label', defaultMessage: 'Walk-on' });
}

/**
 * `Sold out` under the times, for the reader who can take it anyway.
 *
 * <p>In the left column rather than where the status usually goes, because the right-hand side is
 * still carrying something a manager needs — what is left of every capacity, and what the sailing
 * costs. The word belongs with the sailing it describes, under its times.</p>
 */
interface StateNoteProps {
	/**
	 * Sit on the date line rather than below it.
	 *
	 * <p>For the chosen card, whose left column already stacks an eyebrow, a pair of times and a
	 * date — a fifth line for one word made the card noticeably taller than the thing it describes.
	 * In the list the note keeps its own line, where there is no date beside it to share.</p>
	 */
	inline?: boolean;
}

export function TripOptionSoldOutNote({ inline = false }: StateNoteProps = {}) {
	const context = React.useContext(LegOptionContext);
	const intl = useIntl();
	const { isStaff } = useStore();

	if (!context) {
		return null;
	}

	/*
		Already gone, and said even where the row carries a status.

		A customer is told `DEPARTED`; staff are not, because they are allowed to book a sailing
		that has left and `useIsLegSelectable` lets them do exactly that. What a staff member gets
		on a past sailing instead is `SOLD OUT` — true, and not the fact that matters about a ferry
		which has already gone. So this one sits above the guard below and the two appear together:
		the status says it was full, this says it has sailed.

		Tested the way `useIsLegSelectable` tests it rather than on the trip's own flag, so the row
		that says `Past` and the row that can be clicked are always the same row.
	*/
	if (isStaff && (hasDeparted(context) || moment(context.departureTime).isBefore(moment()))) {
		return (
			<span className={noteClass(inline)}>
				{/*
					The app's own word for a sailing that has left, not a second one.

					A customer is told `Departed` here — on this step, on the ferry schedule card and
					in bulk booking — so labelling the same fact `Past` for staff gave one event two
					names and two things for a client to reword. The day rail keeps `Past`, because
					a day does not depart.
				*/}
				<FormattedMessage id="general__departed__label" defaultMessage="Departed" />
			</span>
		);
	}

	/*
		Nothing further where the row already carries a status.

		A customer is told `CLOSED` or `SOLD OUT` in the middle of the row, and saying it twice reads
		as two different facts. This column speaks only for the readers those messages are withheld
		from — see `useLegErrorMessage`, which skips the closed branch for staff and the sold-out
		branch for managers, because both can book past them.
	*/
	if (stringNotEmpty(context.errorMessage)) {
		return null;
	}

	if (isStaff) {
		/*
			`Closed` means shut to everything, not shut to the tab you happen to be on.

			It first read `isTripClosedByBookingType`, which on the vehicle tab is true the moment
			vehicles close — so a sailing still selling 381 passenger seats was labelled CLOSED with
			those 381 seats listed beside it. True of the booking being made, and the wrong word for
			the sailing. The customer-facing status keeps the per-type question, because a customer
			on the vehicle tab genuinely cannot buy anything here; this column is read by someone
			looking at the whole vessel.
		*/
		if (isTripCompletelyClosedFromBookings(context.trip)) {
			return (
				<span className={noteClass(inline)}>
					<FormattedMessage id="trip-option__closed__label" defaultMessage="Closed" />
				</span>
			);
		}

		/*
			Otherwise, name the booking type — and only where it is the one being made.

			`Partially closed` was true of the sailing and useless to the reader: it did not say
			which type, and it fired just as loudly for a closure that could not affect them. A
			walk-on booker has no stake in vehicles being shut, and the row is bookable, so nothing
			is said at all. The names come from the same keys the booking-type tabs use, so a site
			that calls freight `Cargo` reads `Cargo bookings closed`.
		*/
		// The vessel, not the sailing — see `isWalkOnNotOffered`. Staff may still book it, so they
		// are told why it is refused to customers rather than that walk-ons are closed.
		if (isWalkOnNotOffered(context.trip, context.wizardData.tabSelected)) {
			return (
				<span className={noteClass(inline)}>
					<FormattedMessage {...walkOnNotOfferedMessage} />
				</span>
			);
		}

		if (isTripClosedByBookingType(context.trip, context.wizardData.tabSelected)) {
			return (
				<span className={noteClass(inline)}>
					<FormattedMessage
						id="trip-option__type-closed__label"
						defaultMessage="{bookingType} bookings closed"
						values={{ bookingType: bookingTypeName(context.wizardData.tabSelected, intl) }}
					/>
				</span>
			);
		}
	}

	if (!isSoldOutButBookable(context)) {
		return null;
	}

	/*
		Full, or already past full.

		`Sold out` on a sailing carrying two vehicles more than it has room for is not what the
		reader is looking at — and the two call for different decisions, which is the whole reason
		they can still see this row.
	*/
	if (soldOutMargin(context) < 0) {
		return (
			<span className={noteClass(inline, true)}>
				<FormattedMessage id="trip-option__oversold__label" defaultMessage="Oversold" />
			</span>
		);
	}

	return (
		<span className={noteClass(inline)}>
			<FormattedMessage id="trip-option__sold-out__label" defaultMessage="Sold out" />
		</span>
	);
}
